/**
 * @module NotificationPanel
 * @package @geenius/notifications-solidjs
 * @description Renders the compact notification drawer used by the Tailwind
 * SolidJS variant.
 */

import { motion } from '@geenius/motion/solidjs'
import type { Notification } from '@geenius/notifications/shared'
import type { JSX } from 'solid-js'
import { For, onCleanup, onMount, Show } from 'solid-js'
import { createNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'
import { EmptyNotifications } from './EmptyNotifications'
import { NotificationItem } from './NotificationItem'

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS))
    .filter((element) => element.getAttribute('aria-hidden') !== 'true')
}

/**
 * Props for the SolidJS notification panel.
 */
export interface NotificationPanelProps {
  notifications: Notification[]
  onMarkAll?: () => void
  onClose?: () => void
  onRead?: (id: string) => void
  onDismiss?: (id: string) => void
  onViewAll?: () => void
  panelId?: string
  title?: string
  class?: string
}

/**
 * Renders the notification drawer panel.
 *
 * @param props Drawer props including notification data and action handlers.
 * @returns The drawer panel markup.
 */
export function NotificationPanel(props: NotificationPanelProps): JSX.Element {
  const { t } = createNotificationCopy()
  let panelRef: HTMLElement | undefined
  const unreadCount = () =>
    props.notifications.filter((notification) => notification.status === 'unread')
      .length
  const panelId = () => props.panelId ?? 'notifications-panel'
  const titleId = () => `${panelId()}-title`

  onMount(() => {
    const panel = panelRef

    if (!panel) {
      return
    }

    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const focusable = getFocusableElements(panel)

    ;(focusable[0] ?? panel).focus()

    onCleanup(() => {
      previousFocus?.focus()
    })
  })

  const handleKeyDown = (event: KeyboardEvent): void => {
    const panel = panelRef

    if (!panel) {
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      props.onClose?.()
      return
    }

    if (event.key !== 'Tab') {
      return
    }

    const focusable = getFocusableElements(panel)

    if (focusable.length === 0) {
      event.preventDefault()
      panel.focus()
      return
    }

    const firstFocusable = focusable[0]
    const lastFocusable = focusable[focusable.length - 1]
    const activeElement = document.activeElement

    if (event.shiftKey) {
      if (activeElement === firstFocusable || activeElement === panel) {
        event.preventDefault()
        lastFocusable.focus()
      }
      return
    }

    if (activeElement === lastFocusable) {
      event.preventDefault()
      firstFocusable.focus()
    }
  }

  return (
    <motion.section
      ref={panelRef}
      id={panelId()}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId()}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      initial={{ opacity: 0, scale: 0.96, y: 18 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.24 }}
      class={cn(
        'flex max-h-[28rem] w-96 flex-col overflow-hidden rounded-2xl border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface)] shadow-2xl backdrop-blur-xl',
        props.class,
      )}
    >
      <div class="flex items-center justify-between border-b border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface-muted)] px-5 py-4">
        <span id={titleId()} class="flex items-center gap-2 text-sm font-bold tracking-wide text-foreground">
          {props.title ?? t('notifications.common.title')}
          <span class="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary">
            {unreadCount()}
          </span>
        </span>
        <div class="flex items-center gap-3">
          <motion.button
            type="button"
            onClick={() => props.onMarkAll?.()}
            disabled={!props.onMarkAll || unreadCount() === 0}
            whileHover={{ y: -1, scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            class="text-[11px] font-bold uppercase tracking-wide text-muted-foreground transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('notifications.common.markAllRead')}
          </motion.button>
          <motion.button
            type="button"
            onClick={() => props.onClose?.()}
            aria-label={t('notifications.panel.closeAria')}
            whileHover={{ scale: 1.08, rotate: 90 }}
            whileTap={{ scale: 0.95 }}
            class="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--gn-notifications-panel-surface-muted)] p-1.5 text-muted-foreground transition-colors hover:bg-destructive/20 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            ✕
          </motion.button>
        </div>
      </div>
      <div class="custom-scrollbar flex-1 overflow-y-auto overflow-x-hidden bg-[var(--gn-notifications-panel-backdrop)]">
        <Show
          when={props.notifications.length > 0}
          fallback={<EmptyNotifications />}
        >
          <ul>
            <For each={props.notifications}>
              {(notification) => (
                <li class="border-b border-[var(--gn-notifications-panel-border)] last:border-b-0">
                  <NotificationItem
                    notification={notification}
                    onRead={props.onRead}
                    onDismiss={props.onDismiss}
                  />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
      <Show when={props.notifications.length > 0 && props.onViewAll}>
        <div class="border-t border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface-muted)] px-5 py-3 text-center">
          <motion.button
            type="button"
            onClick={() => props.onViewAll?.()}
            whileHover={{ y: -1, scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            class="text-xs font-bold uppercase tracking-widest text-primary transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {t('notifications.common.viewAll')}
          </motion.button>
        </div>
      </Show>
    </motion.section>
  )
}
